/**
 * Compression Service
 *
 * MDSファイルのgzip圧縮・展開を行うサービス
 */

import logger from '../utils/logger';

/**
 * テキストをgzip圧縮する
 *
 * @param text 圧縮するテキスト
 * @returns 圧縮されたバイナリデータ
 * @throws ブラウザがCompressionStreamをサポートしていない場合、または圧縮に失敗した場合
 */
export async function compressGzip(text: string): Promise<Uint8Array> {
  if (typeof CompressionStream === 'undefined') {
    throw new Error(
      'Your browser does not support compression features required by MIDAS. ' +
        'Please update your browser to the latest version.'
    );
  }

  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    const stream = new ReadableStream({
      start(controller) {
        controller.enqueue(data);
        controller.close();
      },
    });

    const compressedStream = stream.pipeThrough(
      new CompressionStream('gzip')
    );

    return await streamToUint8Array(compressedStream);
  } catch (error) {
    logger.error('[CompressionService] Compression failed', { error });
    throw new Error('Failed to compress data.');
  }
}

/**
 * gzip圧縮データを展開する
 *
 * @param data 圧縮されたバイナリデータ
 * @returns 展開されたテキスト
 * @throws ブラウザがDecompressionStreamをサポートしていない場合、または展開に失敗した場合
 */
export async function decompressGzip(data: Uint8Array): Promise<string> {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error(
      'Your browser does not support decompression features required by MIDAS. ' +
        'Please update your browser to the latest version.'
    );
  }

  try {
    const stream = new ReadableStream({
      start(controller) {
        controller.enqueue(data);
        controller.close();
      },
    });

    const decompressedStream = stream.pipeThrough(
      new DecompressionStream('gzip')
    );

    const decompressed = await streamToUint8Array(decompressedStream);
    const decoder = new TextDecoder();
    return decoder.decode(decompressed);
  } catch (error) {
    logger.error('[CompressionService] Decompression failed', { error });
    throw new Error(
      'Failed to decompress MDS file. File may be corrupted or in an incompatible format.'
    );
  }
}

/**
 * ReadableStreamをUint8Arrayに変換する
 *
 * @param stream 変換するストリーム
 * @returns 変換されたバイナリデータ
 */
async function streamToUint8Array(
  stream: ReadableStream<Uint8Array>
): Promise<Uint8Array> {
  const chunks: Uint8Array[] = [];
  const reader = stream.getReader();

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }

  const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }

  return result;
}
